import { MoreHorizontalIcon } from 'lucide-react';
import { Fragment } from 'react';

import { SkuSelectionMethod, type SkuDTO } from '@invenco/products-interface';

import { Badge } from '@/components/core/badge';
import { Button } from '@/components/core/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/core/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/core/dropdown-menu';
import type { Result } from '@/shared/helpers/Result';
import { skuSelectionMethodTitle } from '@/shared/title-maps';

import { SkuBadge } from '../../components/sku-badge';
import { ComponentsTable } from './components-table';
import { isMysteryGroupComponent, type MysteryComponent } from './use-mystery-components';

type Props = {
  sku?: SkuDTO;
  mysteryComponents: MysteryComponent[];
  isLoading?: boolean;
  onAddMysteryComponent?: () => void;
  onEditMysteryComponent?: (index: number) => void;
  onDeleteMysteryComponent?: (index: number) => Promise<Result>;
};

function MysterySectionTitle({
  quantity,
  name,
  optionCount,
  selectionMethods,
}: {
  quantity?: number;
  name?: string;
  optionCount: number;
  selectionMethods?: SkuSelectionMethod[];
}) {
  const principleMethod = selectionMethods?.find(
    (method) => method !== SkuSelectionMethod.MOST_UNIQUE_TO_ORDER,
  );
  const principleMethodLabel = principleMethod
    ? skuSelectionMethodTitle[principleMethod]
    : 'Custom';
  const prefersUnique = selectionMethods?.[0] === SkuSelectionMethod.MOST_UNIQUE_TO_ORDER;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <strong>{quantity ?? 1}</strong>
      <span>x</span>
      <strong>{name ?? 'Component group'}</strong>
      <span>from</span>
      <Badge variant="secondary">{optionCount}</Badge>
      <span>option{optionCount === 1 ? '' : 's'}</span>
      <Badge variant="secondary">{principleMethodLabel}</Badge>
      {prefersUnique ? <Badge variant="secondary">Unique</Badge> : null}
    </div>
  );
}

function FixedSectionTitle({
  quantity,
  skuName,
  skuType,
  skuDescription,
}: {
  quantity?: number;
  skuName?: string;
  skuType?: SkuDTO['type'];
  skuDescription?: string | null;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <strong>{quantity ?? 1}</strong>
      <span>x</span>
      <SkuBadge name={skuName ?? 'Unknown SKU'} type={skuType} />
      {skuDescription ? <span className="truncate text-sm">{skuDescription}</span> : null}
    </div>
  );
}

export function MysteryComponentsCard({
  sku,
  mysteryComponents,
  isLoading,
  onAddMysteryComponent,
  onEditMysteryComponent,
  onDeleteMysteryComponent,
}: Props) {
  if (!sku) return null;
  const componentCount = mysteryComponents.length;

  return (
    <Card aria-label="Components">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span>Components</span>
          <Badge variant="secondary" aria-label={`with ${componentCount} components`}>
            {componentCount}
          </Badge>
        </CardTitle>
        {onAddMysteryComponent ? (
          <CardAction>
            <Button type="button" size="sm" variant="outline" onClick={onAddMysteryComponent}>
              Add component
            </Button>
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {mysteryComponents.map((component, index) => (
          <Fragment key={component.id}>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                {isMysteryGroupComponent(component) ? (
                  <MysterySectionTitle
                    quantity={component.quantity}
                    name={component.name}
                    optionCount={component.components.length}
                    selectionMethods={component.selectionMethods}
                  />
                ) : (
                  <FixedSectionTitle
                    quantity={component.quantity}
                    skuName={component.componentSku?.name}
                    skuType={component.componentSku?.type}
                    skuDescription={component.componentSku?.description}
                  />
                )}
              </div>
              {onDeleteMysteryComponent ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="outline"
                      aria-label="More actions"
                    >
                      <MoreHorizontalIcon />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => void onDeleteMysteryComponent(index)}>
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
              {onEditMysteryComponent ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => onEditMysteryComponent(index)}
                >
                  Manage
                </Button>
              ) : null}
            </div>
            {isMysteryGroupComponent(component) ? (
              <ComponentsTable
                components={component.components}
                skuType={sku.type}
                isLoading={isLoading}
                showTitle={false}
              />
            ) : null}
          </Fragment>
        ))}
      </CardContent>
    </Card>
  );
}
